/*
 * @Description: 神通物联网交易流水
 * @Author: lijinpeng
 * @Date: 2024-04-24 09:46:39
 * @LastEditTime: 2024-04-24 17:41:53
 * @LastEditors: lijinpeng
 */

// 引入api请求方法
import { postTranFlowSTWLList, getTranFlowSTWCriteria } from '@/api/account';
import { formatMoneyConvert } from '@/utils';
import moment from 'moment';

const storeModuleName = 'transactionSTWLModule';

const state = {
  // 筛选项-
  parameters: [
    {
      style: 'select',
      param: 'businessId',
      isFixed: true,
      value: '',
      valueType: 'string',
      state: 'companyList',
      label: '企业',
      placeholder: '请选择',
      noMatchText: '无匹配企业',
      clearable: false,
      filterable: false,
      path: `${storeModuleName}/getCompanyList`,
    },
    {
      style: 'select',
      param: 'channelInfoId',
      value: '',
      valueType: 'string',
      state: 'paymentPlatforms',
      label: '收款方',
      placeholder: '请选择',
      clearable: true,
      filterable: false,
    },
    {
      style: 'select',
      param: 'bizType',
      value: '',
      valueType: 'string',
      state: 'businessTypes',
      label: '交易类型',
      placeholder: '请选择',
      clearable: true,
      filterable: false,
    },
    {
      style: 'select',
      param: 'tallyType',
      value: '',
      valueType: 'string',
      state: 'tradeTypes',
      label: '收支类型',
      placeholder: '请选择',
      clearable: true,
      filterable: false,
    },

    {
      style: 'date-picker',
      param: 'tradeTime',
      value: [],
      valueType: 'date',
      label: '交易时间',
      placeholder: '请选择',
      type: 'daterange',
      format: 'MM-dd',
      valueFormat: 'yyyy-MM-dd',
      clearable: true,
      params: ['tradeStartTime', 'tradeEndTime'],
    },
  ],
  // 导出
  isExport: false,
  // 页码
  postParams: {
    page: 1,
    size: 20,
  },
  // 列表data数据
  listData: {
    list: [],
    tableHeadList: [
      {
        label: '交易编号',
        prop: 'tradeNo',
        width: 140,
        fixed: 'left',
        checked: 1,
        sortable: true,
      },
      {
        label: '交易类型',
        prop: 'bizTypeName',
        width: 100,
        checked: 1,
        sortable: true,
      },
      {
        label: '收支类型',
        prop: 'tallyTypeName',
        width: 100,
        checked: 1,
        sortable: true,
      },
      {
        label: '交易金额（元）',
        prop: 'tradeAmount',
        width: 100,
        checked: 1,
        sortable: true,
        slotName: 'sub-domain-tradeAmount',
      },
      {
        label: '余额（元）',
        prop: 'balanceAfter',
        width: 100,
        checked: 1,
        sortable: true,
      },
      {
        label: '交易时间',
        prop: 'tradeRespTime',
        width: 140,
        checked: 1,
        sortable: true,
      },
      {
        label: '付款方',
        prop: 'businessName',
        width: 140,
        checked: 1,
        sortable: true,
      },
      {
        label: '收款方',
        prop: 'channelName',
        width: 140,
        checked: 1,
        sortable: true,
      },
      {
        label: '支付批次号',
        prop: 'batchId',
        width: 140,
        checked: 1,
        sortable: true,
        slotName: 'sub-domain-batchId',
      },
    ],
  },
  businessId: '', // 所选企业ID
  businessTypes: [], // 交易类型
  paymentPlatforms: [], // 收款平台
  tradeTypes: [], // 收支类型
  statisticsTxt: '', // 统计信息展示
};

// getters
const getters = {};

// actions
const actions = {
  // 获取筛选项数据
  getParams: async context => {
    const params = {};
    context.state.parameters.forEach(item => {
      if (item.style !== 'date-picker' && item.value) {
        if (item.multiple && item.value === 'string') {
          params[item.param] = item.value.toString();
        } else {
          params[item.param] = item.value;
        }
      }
      if (item.style === 'date-picker' && item.value.length > 0) {
        params[item.params[0]] = `${item.value[0]} 00:00:00`;
        params[item.params[1]] = `${item.value[1]} 23:59:59`;
      }
    });
    return params;
  },
  // 获取列表数据
  getList: async context => {
    // 列表展示数据
    const params = await context.dispatch('getParams');
    const { data } = await postTranFlowSTWLList(
      Object.assign({}, params, context.state.postParams),
    );
    context.commit('updateState', {
      key: 'listData',
      value: Object.assign({}, context.state.listData, data),
    });
    // 统计信息
    const abs = true;
    const statisticsTxt = `共计：${data.total}条；其中：收入：${
      data.accountingCount
    }条，${formatMoneyConvert({
      money: data.accountingAmount * 100,
      abs,
    })}元；支出：${data.paymentOutCount}条、${formatMoneyConvert({
      money: data.paymentOutAmount * 100,
      abs,
    })}元；差额：${formatMoneyConvert({
      money: data.difference * 100,
    })}元`;
    context.commit('updateState', {
      key: 'statisticsTxt',
      value: statisticsTxt,
    });
    return data.list;
  },
  // 获取筛选条件
  getQueryCondition: async context => {
    const { code, data } = await getTranFlowSTWCriteria({
      businessId: context.state.businessId,
    });
    if (code === 200) {
      const businessTypes = data['bizType'] || []; // 交易类型
      const paymentPlatforms = data['channel'] || []; // 收款方
      const tradeTypes = data['tallyType'] || []; // 收支类型
      context.commit('updateState', {
        key: 'paymentPlatforms',
        value: paymentPlatforms,
      });
      context.commit('updateState', {
        key: 'businessTypes',
        value: businessTypes,
      });
      context.commit('updateState', {
        key: 'tradeTypes',
        value: tradeTypes,
      });
    }
  },
  // 获取企业列表
  getCompanyList: async (context, payload) => {
    const data = JSON.parse(localStorage.getItem('group'));
    if (data && data.id) {
      // 只展示实名认证的企业列表
      context.commit('updateState', {
        key: 'payPwdExist',
        value: data.payPwdExist,
      });
      context.commit('updateState', {
        key: 'payPwdStatus',
        value: data.payPwdStatus,
      });
      const list = data.corporations.filter(item => item.realNameAudit === 1);
      if (list.length) {
        list.forEach(item => {
          item.label = item.principalPartName;
          item.value = item.businessId;
        });
      }
      context.commit('updateState', {
        key: 'companyList',
        value: list,
      });
    }
  },
};
// mutations
const mutations = {
  // 重置所有状态
  initFilter(state) {
    state.postParams = {
      page: 1,
      size: 20,
    };
    state.parameters.forEach(item => {
      if (item.isFixed) {
        return;
      }
      if (item.style !== 'date-picker' && item.value) {
        if (item.multiple && item.style !== 'input-multiple') {
          item.value = [];
        } else {
          item.value = '';
        }
      }
      if (item.style === 'date-picker') {
        if (item.param === 'tradeTime') {
          item.value = [
            moment().subtract(30, 'day').format('yyyy-MM-DD'),
            moment().format('yyyy-MM-DD'),
          ];
        } else {
          item.value = [];
        }
      }
    });
  },
  // 更新state数据
  updateState(state, { key, value }) {
    state[key] = value;
  },
  // 更新列表参数数据
  updatePostParams(state, payload) {
    state.postParams = Object.assign({}, state.postParams, payload);
  },
  // 根据index更新value
  updateValueFromIndex(state, payload) {
    state.parameters[payload.index].value = payload.value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
